import '@videojs/html/video/player';
import '@videojs/html/video/neutral-skin';
import '@videojs/html/media/hlsjs-video';

/**
 * Enable CMCD so the CDN can correlate its delivery logs
 * with what the player actually experienced. hls.js turns CMCD on by the presence of
 * the config object: it generates a session id per player instance and sends the keys
 * on the request query string.
 */
for (const video of document.querySelectorAll('hlsjs-video')) {
    const source = video.source;
    video.source = {
        ...source,
        src: source?.src ?? video.src,
        engine: {
            ...source?.engine,
            hlsJs: {
                ...source?.engine?.hlsJs,
                cmcd: {},
            },
        },
    };
}

/**
 * Keep the poster overlay the only tab stop, and the only title, until playback has started.
 *
 * The skin shows its control bar for as long as the media is paused, and renders it - along with
 * the container it makes a tab stop of (role="group", tabindex="0") - in shadow DOM, beyond the
 * reach of a stylesheet. `hidden` is the skin's own way out: it lays the control bar out with
 * `display: contents` and overrides that for `[hidden]`, so the bar goes with it out of the tab
 * order. Playback start is read from the play button, which carries the same `data-started` the
 * overlay keys off, so the start screen and the chrome behind it cannot disagree.
 *
 * The skin's title shows whenever the control bar does, so on the start screen it would repeat the
 * overlay's. It rewrites its own `hidden` on every state change, which is why it is held back with
 * an inline `display` instead.
 */
const hideChromeUntilPlaybackStarts = (skin: Element) => {
    const overlay = skin.querySelector<HTMLElement>('.video-player--overlay');
    const playButton = overlay?.querySelector('media-play-button');
    const container = skin.shadowRoot?.querySelector<HTMLElement>('media-container');
    const controls = skin.shadowRoot?.querySelector<HTMLElement>('media-controls');
    const title = skin.shadowRoot?.querySelector<HTMLElement>('media-title');

    if (!overlay || !playButton || !container || !controls || !title) return;

    // Hiding the overlay would drop focus to the top of the document, so remember whether the
    // overlay held focus and hand it over to the player container instead.
    let overlayHasFocus = false;
    overlay.addEventListener('focusin', () => {
        overlayHasFocus = true;
    });
    overlay.addEventListener('focusout', (event) => {
        // A null relatedTarget means focus was lost with the overlay, not moved off it.
        if (event.relatedTarget) overlayHasFocus = false;
    });

    const syncChrome = () => {
        const started = playButton.hasAttribute('data-started');
        container.setAttribute('tabindex', started ? '0' : '-1');
        controls.toggleAttribute('hidden', !started);
        title.style.display = started ? '' : 'none';

        if (started && overlayHasFocus) {
            overlayHasFocus = false;
            container.focus({ preventScroll: true });
        }
    };

    new MutationObserver(syncChrome).observe(playButton, { attributeFilter: ['data-started'] });
    syncChrome();
};

for (const skin of document.querySelectorAll('video-neutral-skin')) {
    hideChromeUntilPlaybackStarts(skin);
}
